import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { BiLockAlt, BiShow, BiHide, BiUser } from 'react-icons/bi';
import { useAuth } from '@/auth/AuthContext';
import { ApiError } from '@/api/errors';
import { Banner } from '@/components/common/Banner';
import './LoginPage.css';

interface FieldErrors {
  email?: string;
  password?: string;
}

/** Maps canonical error codes (07-cross-cutting.md §5) to the login-specific copy in 08-staff-ux.md §3. */
function bannerKeyForError(err: ApiError): string {
  switch (err.code) {
    case 'AUTH_INVALID_CREDENTIALS':
      return 'login.errors.invalidCredentials';
    case 'AUTH_TOO_MANY_ATTEMPTS':
      return 'login.errors.accountLocked';
    case 'AUTH_ACCOUNT_DISABLED':
      return 'login.errors.accountDisabled';
    case 'NETWORK_ERROR':
      return 'login.errors.network';
    default:
      return 'login.errors.generic';
  }
}

export function LoginPage() {
  const { t } = useTranslation();
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [bannerKey, setBannerKey] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  if (status === 'authenticated') {
    const redirect = searchParams.get('redirect');
    return <Navigate to={redirect || '/dashboard'} replace />;
  }

  function validate(): FieldErrors {
    const errors: FieldErrors = {};
    if (!email.trim()) errors.email = t('login.emailRequired');
    if (!password) errors.password = t('login.passwordRequired');
    return errors;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBannerKey(null);

    const errors = validate();
    setFieldErrors(errors);
    if (errors.email) {
      emailRef.current?.focus();
      return;
    }
    if (errors.password) {
      passwordRef.current?.focus();
      return;
    }

    setIsSubmitting(true);
    try {
      await login(email, password, rememberMe);
      const redirect = searchParams.get('redirect');
      navigate(redirect || '/dashboard', { replace: true });
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      setBannerKey(bannerKeyForError(apiError));
      setPassword('');
      passwordRef.current?.focus();
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="fa-login">
      <div className="fa-login__hero">
        <div className="fa-login__hero-content">
          <img className="fa-login__hero-logo" src="/academy-logo.jpeg" alt={t('app.name')} />
          <h2 className="fa-login__hero-title">{t('login.heroTitle')}</h2>
          <p className="fa-login__hero-subtitle">{t('login.heroSubtitle')}</p>
        </div>
      </div>

      <div className="fa-login__form-side">
        <div className="fa-login__card">
          <img className="fa-login__logo" src="/academy-logo.jpeg" alt="" aria-hidden="true" />
          <h1 className="fa-login__title">{t('login.title')}</h1>

          {bannerKey && (
            <Banner tone="danger" assertive>
              {t(bannerKey)}
            </Banner>
          )}

          <form noValidate onSubmit={handleSubmit} className="fa-login__form">
            <div className="fa-field">
              <label htmlFor="login-email">{t('login.email')}</label>
              <div
                className={`fa-login__input-group${
                  fieldErrors.email ? ' fa-login__input-group--invalid' : ''
                }`}
              >
                <span className="fa-login__input-icon" aria-hidden="true">
                  <BiUser />
                </span>
                <input
                  id="login-email"
                  ref={emailRef}
                  type="email"
                  autoComplete="username"
                  className="fa-login__input"
                  value={email}
                  disabled={isSubmitting}
                  aria-invalid={Boolean(fieldErrors.email)}
                  aria-describedby={fieldErrors.email ? 'login-email-error' : undefined}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              {fieldErrors.email && (
                <p id="login-email-error" className="fa-field__error">
                  {fieldErrors.email}
                </p>
              )}
            </div>

            <div className="fa-field">
              <label htmlFor="login-password">{t('login.password')}</label>
              <div
                className={`fa-login__input-group${
                  fieldErrors.password ? ' fa-login__input-group--invalid' : ''
                }`}
              >
                <span className="fa-login__input-icon" aria-hidden="true">
                  <BiLockAlt />
                </span>
                <input
                  id="login-password"
                  ref={passwordRef}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="fa-login__input"
                  value={password}
                  disabled={isSubmitting}
                  aria-invalid={Boolean(fieldErrors.password)}
                  aria-describedby={fieldErrors.password ? 'login-password-error' : undefined}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="fa-login__toggle-password"
                  aria-pressed={showPassword}
                  aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                  onClick={() => setShowPassword((v) => !v)}
                  disabled={isSubmitting}
                >
                  {showPassword ? <BiHide aria-hidden="true" /> : <BiShow aria-hidden="true" />}
                </button>
              </div>
              {fieldErrors.password && (
                <p id="login-password-error" className="fa-field__error">
                  {fieldErrors.password}
                </p>
              )}
            </div>

            <label className="fa-login__remember">
              <input
                type="checkbox"
                checked={rememberMe}
                disabled={isSubmitting}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span>{t('login.rememberMe')}</span>
            </label>

            <button type="submit" className="fa-login__submit" disabled={isSubmitting}>
              {isSubmitting ? t('login.submitting') : t('login.submit')}
            </button>
          </form>

          <p className="fa-login__forgot">{t('login.forgotPassword')}</p>
        </div>
      </div>
    </div>
  );
}
